import type { CarReport, VehicleDefectProfile } from '../api/types'
import { hasDefectProfile, summariseDefects } from './defectProfile'
import { formatColour, formatDate, formatPercent, formatYear, vehicleName } from './format'

/**
 * The 25 points of the printable checklist, in the order of the article that explains them
 * (www.autochecken.com/occasion-kopen/checklist/). The generic PDF is rendered from the same list by
 * `content-seo/scripts/make-checklist.mjs`; a point changed here has to change there and in the
 * article too, or the printed numbers stop matching the explanation a reader looks up.
 */
export interface ChecklistPhase {
  title: string
  hint?: string
  points: ChecklistPoint[]
}

export interface ChecklistPoint {
  number: number
  title: string
  detail?: string
}

const PHASES: [string, string | undefined, [string, string?][]][] = [
  ['Thuis, vóór je gaat kijken', 'Gecheckt op kentekencheck.autochecken.com', [
    ['Advertentie klopt met het kenteken', 'merk, model, brandstof, kleur, bouwjaar'],
    ['APK-historie bekeken', 'steeds hetzelfde gebrek? veel afkeuringen?'],
    ['Tellerstandoordeel RDW', 'logisch / onlogisch / geen oordeel'],
    ['Schadeverleden', 'staat bijna niet in het RDW-register: apart schaderapport'],
    ['Openstaande terugroepacties'],
    ['Geïmporteerd?', 'eerste toelating ↔ toelating in Nederland'],
    ['Toegang milieuzone of zero-emissiezone'],
    ['Zwakke plekken van dit model'],
    ['Prijs vergeleken', 'occasionsites en koerslijst'],
  ]],
  ['De bezichtiging', 'Bij daglicht, liefst droog. Neem een zaklamp en keukenpapier mee.', [
    ['Papieren', 'kentekencard, meldcode, chassisnummer, facturen, 2 sleutels'],
    ['Carrosserie en lak', 'golvingen, naden, spuitnevel, roest'],
    ['Banden en remmen', 'profiel ≥ 3 mm, gelijk afgesleten, remschijven'],
    ['Interieur', 'slijtage past bij km-stand, elektronica, vocht, lampjes'],
    ['Motorruimte', 'lekken, olie, koelvloeistof'],
  ]],
  ['De proefrit', undefined, [
    ['Koude start', 'tikken, rammelen, rook'],
    ['Rijden', 'trekt niet weg, soepel schakelen, geen bonken of zoemen, snelweg'],
    ['Na de rit', 'drupt er iets? nieuwe waarschuwingslampjes?'],
  ]],
  ['Onderhandelen en betalen', undefined, [
    ['Gevonden punten met een bedrag genoemd'],
    ['Onafhankelijke keuring bij twijfel'],
    ['Dealer of particulier: rechten en garantie'],
    ['Veilig betalen', 'nooit vooruit, pas bij het overschrijven'],
    ['Financiering: rente en totaalbedrag'],
  ]],
  ['Na de koop', undefined, [
    ['Overgeschreven', 'kentekenloket, RDW of online met de DigiD-app'],
    ['Verzekerd vóór je wegrijdt'],
    ['Vrijwaringsbewijs aan de verkoper gegeven'],
  ]],
]

export const CHECKLIST: ChecklistPhase[] = (() => {
  let number = 0
  return PHASES.map(([title, hint, points]) => ({
    title,
    hint,
    points: points.map(([pointTitle, detail]) => ({ number: ++number, title: pointTitle, detail })),
  }))
})()

/** How many of the model's commonest rejection defects the printed checklist names. */
const MODEL_DEFECT_LIMIT = 3
/** How many findings of the last inspection are written out before the rest becomes a count. */
const INSPECTION_FINDING_LIMIT = 3

/**
 * What this car's report already answers, keyed by point number. A point the report says nothing
 * about gets no entry rather than a placeholder: the empty checkbox is the honest state for a
 * question that is still the buyer's to answer.
 *
 * The model's weak points are measured over every vehicle sharing the type approval, never on this
 * one, and the note says so in the same line, for the same reason the report's own card does.
 */
export function personalNotes(
  report: CarReport,
  defectProfile?: VehicleDefectProfile,
): Map<number, string> {
  const { identity, registration, inspection, odometer } = report
  const notes = new Map<number, string>()
  const add = (number: number, note: string | undefined) => note && notes.set(number, note)

  const fuel = report.powertrain.energySources?.[0]?.description
  add(
    1,
    joinFacts([
      vehicleName(identity.brand, identity.tradeName),
      formatColour(identity.primaryColour)?.toLowerCase(),
      fuel?.toLowerCase(),
      registration.constructionYear !== undefined
        ? `bouwjaar ${formatYear(registration.constructionYear)}`
        : undefined,
    ]),
  )

  add(
    2,
    joinFacts([
      inspection.apkExpiryDate && `APK geldig tot ${formatDate(inspection.apkExpiryDate)}`,
      inspectionNote(report),
    ]),
  )

  add(3, odometer.judgement && `Oordeel RDW: ${odometer.judgement}`)

  if (registration.hasOpenRecall === true) {
    add(5, 'Er staat een terugroepactie open: vraag of die is uitgevoerd')
  } else if (registration.hasOpenRecall === false) {
    add(5, 'Geen openstaande terugroepactie')
  }

  if (registration.isImported) {
    add(
      6,
      joinFacts([
        'Geïmporteerd',
        registration.firstAdmission && `eerste toelating ${formatDate(registration.firstAdmission)}`,
        registration.firstAdmissionInNetherlands &&
          `in Nederland sinds ${formatDate(registration.firstAdmissionInNetherlands)}`,
      ]),
    )
  } else {
    add(6, 'Niet geïmporteerd')
  }

  if (hasDefectProfile(defectProfile)) {
    const common = summariseDefects(defectProfile.rejectionDefects, MODEL_DEFECT_LIMIT)
    if (common.length > 0) {
      add(
        8,
        `Bij dit model (niet per se deze auto) vaak afgekeurd: ${common
          .map((row) => `${row.label.toLowerCase()} (${formatPercent(row.sharePercent)})`)
          .join('; ')}`,
      )
    }
  }

  add(10, identity.chassisNumberLocation && `Chassisnummer zit: ${identity.chassisNumberLocation.toLowerCase()}`)

  return notes
}

/**
 * The newest inspection's findings in a line. They are "bevindingen", not "gebreken": the RDW's
 * list mixes rejection defects with advice noted at an inspection the car passed, and calling the
 * advice a defect would say something false about the car.
 */
function inspectionNote(report: CarReport): string | undefined {
  const latest = report.inspection.history?.[0]
  if (!latest) return undefined

  const date = formatDate(latest.reportedOn)
  const findings = latest.defects ?? []
  const when = date ? `laatste keuring ${date}` : 'laatste keuring'
  if (findings.length === 0) return `${when}: geen bevindingen`

  const named = findings
    .slice(0, INSPECTION_FINDING_LIMIT)
    .map((finding) => (finding.description ?? `gebrekcode ${finding.code}`).toLowerCase())
  const rest = findings.length - named.length
  return `${when}: ${named.join('; ')}${rest > 0 ? ` en ${rest} meer` : ''}`
}

function joinFacts(parts: (string | undefined | false)[]): string | undefined {
  const present = parts.filter((part): part is string => typeof part === 'string' && part.length > 0)
  return present.length > 0 ? present.join(', ') : undefined
}
